import { EyeInvisibleOutlined, EyeOutlined, PhoneIphone } from 'components/icons';
import Loader from 'components/Loader';
import { useModalBack } from 'components/modals/ModalProvider';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { AnimatePresence, LazyMotion, m } from 'motion/react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { MfaEnrolment } from 'sections/auth/mfa/MfaEnrolment';
import { useGetMfaStatusQuery, useRecordMfaEnrolmentMutation } from 'store/api/user';
import { AlreadyEnrolledError } from 'types/auth';
import { MfaStatus } from 'types/user';
import { cn } from 'utils/cn';
import { cognitoUser } from 'utils/cognitoUser';
import { getLocalStorage } from 'utils/localStorage';
import { t } from 'utils/getTranslations';
import { loadMotionFeatures, SPRING, stepMotion } from 'utils/motion';

type Step = 'status' | 'password' | 'enrol';

type Props = {
    onClose: () => void;
};

/**
 * "My profile › Security": the signed-in user's authenticator. While the tenant's two-factor mode is
 * OPTIONAL, a user without an authenticator can set one up here; that needs a Cognito session, which the
 * portal cookie cannot provide, so the user re-enters the password first. Replacing a lost authenticator
 * is a support task, so an enrolled user only sees the state.
 *
 * <p>Everything here is about the account that typed the password: the backend answers
 * `/v1/users/current/mfa` for the authenticating user even while impersonating, and the password is
 * checked against the same `cognito_user` identity.
 */
export function SecurityModal({ onClose }: Props) {
    const { reauthenticate, discardCognitoSession, user } = useAuth();
    const impersonatedName = user && user.id !== user.authUserId ? `${user.firstName} ${user.lastName}` : null;
    const { data: status, isLoading, isError, refetch } = useGetMfaStatusQuery();
    const [recordEnrolment] = useRecordMfaEnrolmentMutation();
    const dateFormat = useEffectiveDateFormat();
    const [step, setStep] = useState<Step>('status');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // Set once a step has gone back to the status, which then focuses its Set up authenticator.
    const [cameBack, setCameBack] = useState(false);
    const setUpRef = useRef<HTMLButtonElement>(null);
    const email = getLocalStorage('cognito_user') ?? '';
    const isNew = useIsNewDesign();
    const reduced = useReducedMotion();
    const stepsRef = useRef<HTMLDivElement>(null);
    // Unset until measured, so the dialog opens at its first step's height.
    const [stepsHeight, setStepsHeight] = useState<number>();

    // A Cognito session opened for the set-up must not outlive the dialog. Goes to the holder
    // directly: the context's functions are recreated on every render, so depending on one here
    // would run this cleanup mid-flow.
    useEffect(
        () => () => {
            cognitoUser.getCognitoUser()?.signOut(() => {});
            cognitoUser.clear();
        },
        []
    );

    useEffect(() => {
        const steps = stepsRef.current;
        if (!steps) {
            return undefined;
        }
        const observer = new ResizeObserver(([entry]) => setStepsHeight(entry.borderBoxSize[0].blockSize));
        observer.observe(steps);
        return () => observer.disconnect();
    }, [isNew]);

    // On the step's change, not on the button's mount: a status step still fading out is taken back, not mounted anew.
    useEffect(() => {
        if (step === 'status' && cameBack) {
            setUpRef.current?.focus();
        }
    }, [step, cameBack]);

    const backToStatus = () => {
        setStep('status');
        setCameBack(true);
    };

    const cancelPassword = () => {
        setPassword('');
        setShowPassword(false);
        setError(null);
        backToStatus();
    };

    // Escape on the password step does what its Cancel does, and closes the dialog while Cancel is off.
    const bodyRef = useModalBack<HTMLDivElement>(step === 'password' && !busy ? cancelPassword : undefined);

    const submitPassword = async (event: FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await reauthenticate(password);
            setPassword('');
            setStep('enrol');
        } catch (err: unknown) {
            console.error('Re-authentication failed:', err);
            setError(describeReauthError(err));
        } finally {
            setBusy(false);
        }
    };

    const enrolledAtLabel = (() => {
        if (!status?.enrolledAt) {
            return null;
        }
        try {
            return format(new Date(status.enrolledAt), dateFormat);
        } catch {
            return null;
        }
    })();

    let body;
    if (step === 'enrol') {
        body = (
            <MfaEnrolment
                email={email}
                animated={isNew}
                onEnrolled={async () => {
                    await recordEnrolment().unwrap();
                }}
                continueLabel={t('done')}
                onContinue={onClose}
                cancelLabel={t('cancel')}
                onCancel={() => {
                    discardCognitoSession();
                    backToStatus();
                }}
            />
        );
    } else if (step === 'password') {
        body = (
            <form
                onSubmit={(event) => {
                    void submitPassword(event);
                }}
            >
                <h2 className="text-lg font-semibold">{t('auth.mfa-confirm-password-title')}</h2>
                <p className="text-muted-foreground mt-2 mb-6 text-sm">{t('auth.mfa-confirm-password-description')}</p>
                <label htmlFor="mfa-reauth-password" className="text-sm font-medium">
                    {t('auth.password')}
                </label>
                <div className="relative mt-2">
                    <Input
                        id="mfa-reauth-password"
                        className="pr-10"
                        autoFocus
                        autoComplete="current-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        aria-invalid={Boolean(error)}
                        onChange={(event) => {
                            setPassword(event.target.value);
                            setError(null);
                        }}
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="absolute top-0.5 right-0.5"
                        aria-label={t('auth.show-password')}
                        aria-pressed={showPassword}
                        onClick={() => setShowPassword((value) => !value)}
                    >
                        <span className="t-icon-swap" data-state={showPassword ? 'b' : 'a'} aria-hidden="true">
                            <span className="t-icon" data-icon="a">
                                <EyeInvisibleOutlined />
                            </span>
                            <span className="t-icon" data-icon="b">
                                <EyeOutlined />
                            </span>
                        </span>
                    </Button>
                </div>
                {error ? (
                    <p className="text-destructive mt-2 text-xs" role="alert">
                        {error}
                    </p>
                ) : null}
                <div className="mt-6 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={cancelPassword} disabled={busy}>
                        {t('cancel')}
                    </Button>
                    <Button type="submit" disabled={busy || !password}>
                        {t('continue')}
                    </Button>
                </div>
            </form>
        );
    } else if (isError) {
        body = (
            <div className="flex flex-col items-start gap-3">
                <p className="text-destructive text-sm" role="alert">
                    {t('user.security-status-unavailable')}
                </p>
                <Button
                    variant="outline"
                    onClick={() => {
                        void refetch();
                    }}
                >
                    {t('retry')}
                </Button>
            </div>
        );
    } else if (isLoading || !status) {
        body = (
            <div className="h-20">
                <Loader />
            </div>
        );
    } else {
        const canSetUp = !status.enrolled && !status.ssoManaged && status.mode !== 'OFF';
        body = (
            <>
                {impersonatedName ? (
                    <p className="bg-muted text-muted-foreground mb-4 rounded-md px-3 py-2 text-xs">
                        {t('user.security-impersonation-note', { name: impersonatedName })}
                    </p>
                ) : null}
                <div className="border-border flex items-center gap-4 border-t py-4">
                    <div className="bg-success-light flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]">
                        <PhoneIphone className="text-primary text-[22px]" />
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-semibold">{t('auth.mfa-authenticator-app')}</div>
                        <div className="text-muted-foreground text-xs">
                            {status.ssoManaged
                                ? t('user.security-authenticator-sso')
                                : status.enrolled
                                  ? enrolledAtLabel
                                      ? t('user.security-authenticator-added', { date: enrolledAtLabel })
                                      : t('user.security-authenticator-active')
                                  : status.mode === 'REQUIRED'
                                    ? t('user.security-authenticator-required')
                                    : t('user.security-authenticator-optional')}
                        </div>
                    </div>
                    <span
                        className={cn(
                            'shrink-0 rounded-md px-2.5 py-1 text-xs font-semibold',
                            status.enrolled ? 'bg-success-light text-primary' : 'bg-muted text-muted-foreground'
                        )}
                    >
                        {status.ssoManaged
                            ? t('user.security-status-sso')
                            : status.enrolled
                              ? t('user.security-status-active')
                              : t('user.security-status-not-set-up')}
                    </span>
                </div>
                {canSetUp ? (
                    <div className="mt-5 flex gap-3">
                        <Button ref={setUpRef} variant="outline" onClick={() => setStep('password')}>
                            {t('user.security-set-up-authenticator')}
                        </Button>
                    </div>
                ) : null}
                <p className="text-muted-foreground mt-3 text-xs">{statusNote(status)}</p>
            </>
        );
    }

    return (
        <>
            <div className="border-border flex items-center justify-between border-b py-2.5 pr-4 pl-6">
                <h1 className="text-base font-semibold">{t('user.security-title')}</h1>
                <Button variant="ghost" size="icon-sm" aria-label={t('close')} onClick={onClose}>
                    <span aria-hidden="true" className="text-lg leading-none">
                        ×
                    </span>
                </Button>
            </div>
            {/* Password managers animate a filled input with a transient `transform: scale(1.2)`; kept out of
                the horizontal overflow so the scaled input paints past the padding without a scrollbar. */}
            <div ref={bodyRef} className="flex flex-col overflow-x-hidden overflow-y-auto p-6 max-sm:px-4">
                {isNew ? (
                    <LazyMotion features={loadMotionFeatures} strict>
                        {/* Goes to the height measured inside it, never to `auto`, clipping the steps as they
                            change; 4px of room above and below keeps the focus rings at its edges in. */}
                        <m.div
                            className="-my-1 shrink-0 overflow-y-clip"
                            initial={false}
                            animate={stepsHeight === undefined ? undefined : { height: stepsHeight }}
                            transition={reduced ? { duration: 0 } : SPRING}
                        >
                            <div ref={stepsRef} className="relative py-1">
                                <AnimatePresence initial={false} mode="popLayout">
                                    <m.div key={step} {...stepMotion(reduced)}>
                                        {body}
                                    </m.div>
                                </AnimatePresence>
                            </div>
                        </m.div>
                    </LazyMotion>
                ) : (
                    body
                )}
            </div>
        </>
    );
}

function statusNote(status: MfaStatus): string {
    if (status.ssoManaged) {
        return t('user.security-sso-note');
    }
    if (status.enrolled) {
        return t('user.security-replace-support-note');
    }
    switch (status.mode) {
        case 'OPTIONAL':
            return t('user.security-set-up-note');
        case 'REQUIRED':
            return t('user.security-required-note');
        default:
            return t('user.security-off-note');
    }
}

/**
 * Only Cognito's `NotAuthorizedException` means the password was wrong; a required password change or a
 * service failure gets the general message, so the user is not told to retype a correct password.
 */
function describeReauthError(err: unknown): string {
    if (err instanceof AlreadyEnrolledError) {
        return t('auth.mfa-already-enrolled');
    }
    const code = typeof err === 'object' && err !== null && 'code' in err ? String(err.code) : '';
    return code === 'NotAuthorizedException' ? t('auth.mfa-password-incorrect') : t('auth.mfa-reauth-failed');
}
